import { faFileArrowDown, faFileChartColumn, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { Grid, Typography, useMediaQuery } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { CalendarFilter } from 'components/UI';
import Button from 'components/UI/V3/Button/Button';
import { Dropdown, DropdownContent, DropdownTrigger } from 'components/UI/V3/Dropdown';
import { DropdownItem } from 'components/UI/V3/Dropdown/Item/DropdownItem';
import { Period } from '@spike/model';
import { CashReport, AccrualReport } from '@spike/payments-model';
import moment from 'moment-timezone';
import { FunctionComponent, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useMarketplace, useTimeZone } from 'hooks';
import { faCalendarDays } from '@fortawesome/pro-regular-svg-icons';
import { useDateRangeOptions } from 'hooks/useDateRangeOptions';
import { useActivityTimeline } from 'components/Activities/Timeline/ActivityTimelineProvider';
import { DateRangeOption, DateRangePickerDropdown } from 'components/UI/V3/DateRangePickerDropdown';
import React from 'react';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import OrganizationMarketplacesFilter from 'components/Bookings2/List/OrganizationMarketplacesFilter';

interface HeaderProps {
    cashReport?: CashReport;
    accrualReport?: AccrualReport;
    reportType?: 'cash' | 'accrual';
    selectedMarketplaceIds: Array<number>;
    hideExport?: boolean;
    className?: string;
    onChangePeriod?: (period: Period | null) => void;
    onReportTypeChange?: (reportType: 'cash' | 'accrual', period: Period | null) => void;
    onChangeMarketplaceIds?: (marketplaceIds: Array<number>, period: Period | null) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        bar: {
            display: 'flex',
            borderRadius: 12,
            padding: '12px 16px',
            justifyContent: 'space-between',
            backgroundColor: '#F1F1F1',

            [theme.breakpoints.up('md')]: {
                marginTop: 24,
                padding: '14px 20px'
            }
        },
        dateContainer: {
            marginTop: 24,

            [theme.breakpoints.up('md')]: {
                marginTop: '45px'
            }
        },
        date: {
            color: '#222',
            fontSize: 20,
            lineHeight: 1,
            fontWeight: 600,

            [theme.breakpoints.up('md')]: {
                fontSize: '26px'
            }
        },
        calendar: {
            '& > button': {
                'color': '#000',
                'border': '2px solid #000',
                'backgroundColor': 'white',
                '&:hover': {
                    backgroundColor: 'white'
                }
            }
        },
        leftGroup: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        reportSelector: {
            'color': '#000',
            'borderColor': '#000',
            'backgroundColor': 'white',
            'borderRadius': '31px',

            'padding': '8px 12px',
            'height': '40px',
            '&:hover': {
                backgroundColor: 'white !important',
                color: '#000',
                borderColor: '#000'
            },
            '&:focus': {
                backgroundColor: 'white',
                color: '#000',
                borderColor: '#000',
                outline: 'none'
            },
            '&:active': {
                backgroundColor: 'white',
                color: '#000',
                borderColor: '#000'
            },

            [theme.breakpoints.down('sm')]: {
                'padding': '6px 10px',
                'height': '38px',
                'borderRadius': '20px',
                'fontSize': '14px',
                '& span': {
                    fontSize: '14px !important',
                    lineHeight: 1
                }
            }
        },
        reportOptions: {
            [theme.breakpoints.down('md')]: {
                'fontSize': '12px !important',

                '& [class*="root"]': {
                    fontSize: '12px !important',
                    padding: '8px 16px',
                    minHeight: 'unset'
                },

                '& [class*="label"]': {
                    fontSize: '12px !important'
                }
            },
            [theme.breakpoints.down('sm')]: {
                'paddingLeft': '0px',
                'marginLeft': '0px',
                'fontSize': '10px !important',

                '& [class*="root"]': {
                    fontSize: '10px !important',
                    padding: '8px 16px',
                    minHeight: 'unset'
                },

                '& [class*="label"]': {
                    fontSize: '10px !important'
                }
            }
        },
        csv: {
            'textDecoration': 'none',

            '& button': {
                [theme.breakpoints.down('sm')]: {
                    'height': 40,
                    'width': 40,
                    'padding': '0px',
                    'minWidth': '40px',
                    'borderRadius': '50%',
                    'backgroundColor': '#000',
                    'color': '#fff',
                    'border': 'none',

                    '&:hover': {
                        backgroundColor: '#000',
                        color: '#fff'
                    },

                    '& span': {
                        // !important used due MUI adds negative margin
                        margin: '0px !important',
                        fontSize: 14,
                        lineHeight: 1,
                        display: 'none'
                    },
                    '& svg': {
                        width: 16,
                        height: 16,
                        marginRight: 0,
                        color: '#fff'
                    }
                }
            }
        },
        csvButton: {
            [theme.breakpoints.down('md')]: {
                'height': 39,
                'minWidth': 'unset',
                'borderRadius': 30,
                'width': 39,
                'padding': 0,
                '& span': {
                    lineHeight: 1,
                    margin: '0px !important'
                },
                '& svg': {
                    width: 16,
                    height: 'auto',
                    marginRight: '0px !important'
                },
                '& .MuiButton-startIcon': {
                    marginLeft: '2px !important'
                }
            }
        },
        marketplacesFilter: {
            '& > button': {
                fontSize: 16,
                height: 41
            }
        }
    })
);

export const Header: FunctionComponent<HeaderProps> = props => {
    const classes = useStyles();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
    const timeZone = useTimeZone();
    const marketplace = useMarketplace();
    const [range, setRange] = React.useState({
        from: moment.tz(timeZone).startOf('day'),
        to: moment.tz(timeZone).endOf('day')
    });
    const [dateRange, setDateRange] = React.useState<DateRangeOption['value']>(range);
    const { todayOption, thisMonthOption, last30DaysOption, allTimeOption } = useDateRangeOptions();

    const [period, setPeriod] = useState<Period | null>({
        from: moment(),
        to: moment()
    });

    const [selectedReportType, setSelectedReportType] = useState<'cash' | 'accrual'>('cash');

    const reportOptions = [
        { id: 'cash', label: 'Cash Report' },
        { id: 'accrual', label: 'Accrual Report' }
    ];

    // CSV Filename for Cash Report
    const csvFileNameCash = `cash_report_${props.cashReport?.from?.format('YYYY-MM-DD') || 'all'}_${
        props.cashReport?.to?.format('YYYY-MM-DD') || 'time'
    }.csv`;

    // CSV Filename for Accrual Report
    const csvFileNameAccrual = `accrual_report_${props.accrualReport?.from?.format('YYYY-MM-DD') || 'all'}_${
        props.accrualReport?.to?.format('YYYY-MM-DD') || 'time'
    }.csv`;

    // CSV Headers for Cash Report
    const csvHeadersCash = [
        { label: 'Services', key: 'services' },
        { label: 'Add-On Services', key: 'addonServices' },
        { label: 'Memberships', key: 'memberships' },
        { label: 'Products', key: 'products' },
        { label: 'Custom Items', key: 'customItems' },
        { label: 'Taxes', key: 'taxes' },
        { label: 'Surcharges Collected', key: 'surchargesCollected' },
        { label: 'Tips', key: 'tips' },
        { label: 'Cuddles Fees', key: 'cuddlesFees' },
        { label: 'Payouts', key: 'payouts' },
        { label: 'Refunds', key: 'refunds' },
        { label: 'Cash Payments', key: 'cashPayments' },
        { label: 'Credit Card Payments', key: 'creditCardPayments' },
        { label: 'POS Payments', key: 'posPayments' },
        { label: 'Pay By Link Payments', key: 'payByLinkPayments' },
        { label: 'Other Payments', key: 'otherPayments' },
        { label: 'Cash Refunds', key: 'cashRefunds' },
        { label: 'Credit Card Refunds', key: 'creditCardRefunds' },
        { label: 'POS Refunds', key: 'posRefunds' },
        { label: 'Pay By Link Refunds', key: 'payByLinkRefunds' },
        { label: 'Other Refunds', key: 'otherRefunds' },
        { label: 'From', key: 'from' },
        { label: 'To', key: 'to' }
    ];

    // CSV Headers for Accrual Report
    const csvHeadersAccrual = [
        { label: 'Enrollment - Total Recognized', key: 'enrollmentTotalRecognized' },
        { label: 'Enrollment - Total Discount Recognized', key: 'enrollmentTotalDiscountRecognized' },
        { label: 'Enrollment - Total Deferred', key: 'enrollmentTotalDeferred' },
        { label: 'Enrollment - Total Deferred Discount', key: 'enrollmentTotalDeferredDiscount' },
        { label: 'Services - Total Recognized', key: 'servicesTotalRecognized' },
        { label: 'Services - Total Discount Recognized', key: 'servicesTotalDiscountRecognized' },
        { label: 'Services - Total Deferred', key: 'servicesTotalDeferred' },
        { label: 'Services - Total Deferred Discount', key: 'servicesTotalDeferredDiscount' },
        { label: 'Add-On Services - Total Recognized', key: 'addonServicesTotalRecognized' },
        { label: 'Add-On Services - Total Discount Recognized', key: 'addonServicesTotalDiscountRecognized' },
        { label: 'Add-On Services - Total Deferred', key: 'addonServicesTotalDeferred' },
        { label: 'Add-On Services - Total Deferred Discount', key: 'addonServicesTotalDeferredDiscount' },
        { label: 'Products - Total Recognized', key: 'productsTotalRecognized' },
        { label: 'Products - Total Discount Recognized', key: 'productsTotalDiscountRecognized' },
        { label: 'Products - Total Deferred', key: 'productsTotalDeferred' },
        { label: 'Products - Total Deferred Discount', key: 'productsTotalDeferredDiscount' },
        { label: 'Custom Items - Total Recognized', key: 'customItemsTotalRecognized' },
        { label: 'Custom Items - Total Discount Recognized', key: 'customItemsTotalDiscountRecognized' },
        { label: 'Custom Items - Total Deferred', key: 'customItemsTotalDeferred' },
        { label: 'Custom Items - Total Deferred Discount', key: 'customItemsTotalDeferredDiscount' },
        { label: 'Collected Tax', key: 'collectedTax' },
        { label: 'Totals - Recognized', key: 'totalsRecognized' },
        { label: 'Totals - Discount Recognized', key: 'totalsDiscountRecognized' },
        { label: 'Totals - Deferred', key: 'totalsDeferred' },
        { label: 'Totals - Deferred Discount', key: 'totalsDeferredDiscount' },
        { label: 'From', key: 'from' },
        { label: 'To', key: 'to' }
    ];

    // CSV Data for Cash Report
    const csvDataCash = [
        {
            services: props.cashReport?.totals?.serviceAmount ?? 0,
            addonServices: props.cashReport?.totals?.addonAmount ?? 0,
            memberships: props.cashReport?.totals?.membershipAmount ?? 0,
            products: props.cashReport?.totals?.productAmount ?? 0,
            customItems: props.cashReport?.totals?.customAmount ?? 0,
            taxes: props.cashReport?.totals?.taxAmount ?? 0,
            surchargesCollected: props.cashReport?.totals?.surchargeAmount ?? 0,
            tips: props.cashReport?.totals?.tipAmount ?? 0,
            cuddlesFees: props.cashReport?.totals?.cuddlespayFee ?? 0,
            payouts: props.cashReport?.totals?.payoutAmount ?? 0,
            refunds: props.cashReport?.totals?.refundAmount ?? 0,
            cashPayments: props.cashReport?.payments?.cash ?? 0,
            creditCardPayments: props.cashReport?.payments?.creditCard ?? 0,
            posPayments: props.cashReport?.payments?.pos ?? 0,
            payByLinkPayments: props.cashReport?.payments?.payByLink ?? 0,
            otherPayments: props.cashReport?.payments?.other ?? 0,
            cashRefunds: props.cashReport?.refunds?.cash ?? 0,
            creditCardRefunds: props.cashReport?.refunds?.creditCard ?? 0,
            posRefunds: props.cashReport?.refunds?.pos ?? 0,
            payByLinkRefunds: props.cashReport?.refunds?.payByLink ?? 0,
            otherRefunds: props.cashReport?.refunds?.other ?? 0,
            from: props.cashReport?.from?.format('YYYY-MM-DD') || '',
            to: props.cashReport?.to?.format('YYYY-MM-DD') || ''
        }
    ];

    // CSV Data for Accrual Report
    const csvDataAccrual = [
        {
            enrollmentTotalRecognized: props.accrualReport?.sales?.enrollment?.totalRecognized ?? 0,
            enrollmentTotalDiscountRecognized: props.accrualReport?.sales?.enrollment?.totalDiscountRecognized ?? 0,
            enrollmentTotalDeferred: props.accrualReport?.sales?.enrollment?.totalDeferred ?? 0,
            enrollmentTotalDeferredDiscount: props.accrualReport?.sales?.enrollment?.totalDeferredDiscount ?? 0,
            servicesTotalRecognized: props.accrualReport?.sales?.service?.totalRecognized ?? 0,
            servicesTotalDiscountRecognized: props.accrualReport?.sales?.service?.totalDiscountRecognized ?? 0,
            servicesTotalDeferred: props.accrualReport?.sales?.service?.totalDeferred ?? 0,
            servicesTotalDeferredDiscount: props.accrualReport?.sales?.service?.totalDeferredDiscount ?? 0,
            addonServicesTotalRecognized: props.accrualReport?.sales?.addonService?.totalRecognized ?? 0,
            addonServicesTotalDiscountRecognized:
                props.accrualReport?.sales?.addonService?.totalDiscountRecognized ?? 0,
            addonServicesTotalDeferred: props.accrualReport?.sales?.addonService?.totalDeferred ?? 0,
            addonServicesTotalDeferredDiscount: props.accrualReport?.sales?.addonService?.totalDeferredDiscount ?? 0,
            productsTotalRecognized: props.accrualReport?.sales?.businessProduct?.totalRecognized ?? 0,
            productsTotalDiscountRecognized: props.accrualReport?.sales?.businessProduct?.totalDiscountRecognized ?? 0,
            productsTotalDeferred: props.accrualReport?.sales?.businessProduct?.totalDeferred ?? 0,
            productsTotalDeferredDiscount: props.accrualReport?.sales?.businessProduct?.totalDeferredDiscount ?? 0,
            customItemsTotalRecognized: props.accrualReport?.sales?.customItem?.totalRecognized ?? 0,
            customItemsTotalDiscountRecognized: props.accrualReport?.sales?.customItem?.totalDiscountRecognized ?? 0,
            customItemsTotalDeferred: props.accrualReport?.sales?.customItem?.totalDeferred ?? 0,
            customItemsTotalDeferredDiscount: props.accrualReport?.sales?.customItem?.totalDeferredDiscount ?? 0,
            collectedTax: props.accrualReport?.collectedTax ?? 0,
            totalsRecognized: props.accrualReport?.totals?.recognized ?? 0,
            totalsDiscountRecognized: props.accrualReport?.totals?.discountRecognized ?? 0,
            totalsDeferred: props.accrualReport?.totals?.deferred ?? 0,
            totalsDeferredDiscount: props.accrualReport?.totals?.deferredDiscount ?? 0,
            from: props.accrualReport?.from?.format('YYYY-MM-DD') || '',
            to: props.accrualReport?.to?.format('YYYY-MM-DD') || ''
        }
    ];

    const changePeriodHandler = (period: Period | null) => {
        setDateRange(period);
        if (period) {
            setPeriod({ from: period.from.clone(), to: period.to.clone() });
            props.onChangePeriod &&
                props.onChangePeriod({
                    from: period.from.clone(),
                    to: period.to.clone()
                });
        } else {
            setPeriod({ from: moment(marketplace.createdAt, 'YYYY-MM-DD'), to: moment.tz(timeZone).startOf('day') });
            props.onChangePeriod &&
                props.onChangePeriod({
                    from: moment(marketplace.createdAt, 'YYYY-MM-DD'),
                    to: moment.tz(timeZone).startOf('day')
                });
        }
    };

    const handleReportTypeChange = (reportType: 'cash' | 'accrual') => {
        setSelectedReportType(reportType);
        props.onReportTypeChange && props.onReportTypeChange(reportType, period);
    };

    const selectedOption = reportOptions.find(option => option.id === selectedReportType) || reportOptions[0];

    return (
        <Grid
            container
            className={props.className}
        >
            <Grid
                container
                xs={12}
                className={classes.bar}
            >
                <div className={classes.leftGroup}>
                    <DateRangePickerDropdown
                        timeZone={timeZone}
                        value={dateRange}
                        onChange={changePeriodHandler}
                        options={[todayOption, thisMonthOption, last30DaysOption, allTimeOption]}
                        buttonProps={{
                            'variant': 'outlined',
                            'leftIcon': faCalendarDays,
                            'aria-label': 'Date range filter'
                        }}
                    />
                    {/*<CalendarFilter
                        disableFuture
                        showLabelOnMobile
                        hideUpcoming
                        dropdownAlignment="left"
                        optionSelected="Today"
                        period={{ from: moment(), to: moment() }}
                        onChange={changePeriodHandler}
                        className={classes.calendar}
                    />*/}
                    <Dropdown>
                        <DropdownTrigger>
                            <Button
                                label={selectedOption.label}
                                leftIcon={faFileChartColumn}
                                className={classes.reportSelector}
                            />
                        </DropdownTrigger>
                        <DropdownContent
                            className={classes.reportOptions}
                            minWidth={140}
                        >
                            {reportOptions.map(option => (
                                <DropdownItem
                                    key={option.id}
                                    onSelect={() => handleReportTypeChange(option.id as 'cash' | 'accrual')}
                                >
                                    {option.label}
                                </DropdownItem>
                            ))}
                        </DropdownContent>
                    </Dropdown>
                    <OrganizationMarketplacesFilter
                        className={classes.marketplacesFilter}
                        selected={props.selectedMarketplaceIds}
                        onChangeSelection={marketplaceIds => props.onChangeMarketplaceIds?.(marketplaceIds, period)}
                    />
                </div>
                {!props.hideExport && (
                    <CSVLink
                        data={props.reportType === 'cash' ? csvDataCash : csvDataAccrual}
                        headers={props.reportType === 'cash' ? csvHeadersCash : csvHeadersAccrual}
                        enclosingCharacter=""
                        separator=","
                        filename={props.reportType === 'cash' ? csvFileNameCash : csvFileNameAccrual}
                        target="_blank"
                        className={classes.csv}
                    >
                        <Button
                            leftIcon={faFileArrowDown}
                            variant="black"
                            label={isMobile ? '' : 'Export'}
                            size={isMobile ? 'small' : 'large'}
                            className={classes.csvButton}
                        />
                    </CSVLink>
                )}
            </Grid>
            <Grid
                item
                xs={12}
                className={classes.dateContainer}
            >
                <Typography className={classes.date}>
                    {period === null
                        ? 'All Time'
                        : period.from.clone().isSame(period.to.clone(), 'day')
                        ? period.from.format('MMMM DD, YYYY')
                        : `${period.from.format('MMMM DD, YYYY')} - ${period.to.format('MMMM DD, YYYY')}`}
                </Typography>
            </Grid>
        </Grid>
    );
};

export default Header;
